import { Icon } from '@dixa/lib-shared-habla';
import { SearchUser } from 'lib-search-types';
import { ComponentProps, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';

import { searchApi } from 'apis/search';
import {
  HookFormSearchSelect,
  HookFormSearchSelectResult,
} from 'components/hookForm';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { FormValues } from '../composeEmailModal.types';

import { ContactItemContent } from './contactItemContent';

type ComponontPropsType = Omit<
  ComponentProps<typeof HookFormSearchSelect>['componentProps'],
  'getItemTitleByValue'
>;

type ComposeEmailFieldEmailContactProps = {
  name: 'toEmail' | 'cc' | 'bcc';
  label: string;
  multiple?: boolean;
  required?: boolean;
  componentProps?: ComponontPropsType;
};

const createSearchUser = (input: string): SearchUser => ({
  id: 'suggestion',
  name: input,
  email: input,
  type: 'contact',
  org_id: null,
  phone_number: null,
  avatar_url: null,
  company_id: null,
  default_outbound_number: null,
  anonymized_at: null,
  given_name: null,
  family_name: null,
  has_scim_enabled: false,
  external_id: null,
});

const isSearchUser = (
  result: HookFormSearchSelectResult,
): result is SearchUser => {
  if (result && typeof result === 'object' && 'email' in result) {
    return true;
  }
  return false;
};

export const ComposeEmailFieldEmailContact = ({
  name,
  label,
  multiple = false,
  required = false,
  componentProps,
}: ComposeEmailFieldEmailContactProps): JSX.Element => {
  const { control, getValues, setValue } = useFormContext<FormValues>();
  const dispatch = useAppDispatch();
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [filterValue, setFilterValue] = useState('');

  const commitName = useCallback(() => {
    if (!editingEmail) return;
    setValue(
      name,
      getValues(name).map((e) =>
        e.email === editingEmail ? { ...e, name: editingName || e.email } : e,
      ),
    );
    setEditingEmail(null);
  }, [editingEmail, editingName, getValues, name, setValue]);

  const getSuggestions = useCallback(
    (searchTerm: string, results: readonly SearchUser[]) => {
      // filterValue is synchronous; searchTerm lags 300ms — use the fresher one so the suggestion exists before the debounce fires.
      const term = filterValue || searchTerm;
      if (isEmail(term)) {
        const suggestedUser = createSearchUser(term);

        const existsInResults = results.some(
          (result) =>
            result.email?.toLowerCase() === term.toLowerCase() ||
            result.additional_emails?.some(
              (e) => e.toLowerCase() === term.toLowerCase(),
            ),
        );

        if (!existsInResults) {
          return [suggestedUser];
        }
      }

      return [];
    },
    [filterValue],
  );

  const handleBlur = useCallback(async () => {
    if (!filterValue || !isEmail(filterValue)) return;
    const currentValues = getValues(name);
    const searchTermAlreadyAdded = currentValues.some(
      (v) =>
        v.email.toLowerCase() === filterValue.toLowerCase() &&
        v.id !== 'suggestion',
    );
    if (searchTermAlreadyAdded) {
      setFilterValue('');
      return;
    }

    const lookup = dispatch(
      searchApi.endpoints.readSearchUsers.initiate(
        {
          pageSize: 1,
          filters: {
            strategy: 'all',
            filters: [
              {
                field: 'user_type',
                operator: 'is_one',
                values: [{ value: 'member' }, { value: 'contact' }],
              },
              {
                field: 'email',
                operator: 'is_one',
                values: [{ value: filterValue.toLowerCase() }],
              },
            ],
          },
        },
        { forceRefetch: true },
      ),
    );
    lookup.unsubscribe();

    const contact = await (async () => {
      try {
        const response = await lookup;
        const found = response.data?.data?.results?.[0]?.result;
        if (found?.email?.trim()) {
          return {
            email: found.email.trim(),
            name: found.name || found.email.trim(),
            id: found.id,
          };
        }
      } catch {
        // fall through to suggestion
      }
      return { email: filterValue, name: '', id: 'suggestion' as const };
    })();

    // Re-read after the await: the user may have added/removed values while the
    // API call was in flight.
    const latestValues = getValues(name);

    // Skip if already resolved as a real contact, or if the lookup only
    // returned a fallback suggestion that's already in the list.
    const contactAlreadyAdded = latestValues.some((v) => {
      if (v.email.toLowerCase() !== contact.email.toLowerCase()) return false;
      return v.id !== 'suggestion' || contact.id === 'suggestion';
    });
    if (contactAlreadyAdded) {
      setFilterValue('');
      return;
    }

    // Remove any stale suggestion for this email before adding the resolved entry.
    const baseValues = multiple
      ? latestValues.filter(
          (v) => v.email.toLowerCase() !== contact.email.toLowerCase(),
        )
      : [];
    setValue(name, [...baseValues, contact], { shouldValidate: true });
    setFilterValue('');
  }, [
    dispatch,
    filterValue,
    getValues,
    multiple,
    name,
    setFilterValue,
    setValue,
  ]);

  return (
    <HookFormSearchSelect
      control={control}
      name={name}
      label={label}
      normalizeCasing
      rules={
        required
          ? {
              required: 'Please select an email address',
            }
          : undefined
      }
      componentProps={{
        ...componentProps,
        multiple,
        clearable: true,
        autoFocus: getValues(name).length === 0,
        placeholder: 'Select or type an email address',
        equalityFn: (a, b) => a?.email === b?.email,
        filter: {
          onChange: (e) => setFilterValue(e.target.value.trim()),
          onKeyDown: (e) => {
            if (e.key !== 'Enter') return;
            if (isEmail(filterValue)) {
              void handleBlur();
            } else if (filterValue.includes('@')) {
              // Incomplete email address — block SelectionList from picking a random contact
              e.stopPropagation();
            }
          },
        },
        onBlur: handleBlur,
        getItemTitleByValue: (value) => {
          if (!value) return '';

          if (value.id === 'suggestion' && editingEmail === value.email) {
            return (
              <input
                autoFocus
                type="text"
                value={editingName}
                placeholder="Add a name..."
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitName();
                  }
                  if (e.key === 'Escape') setEditingEmail(null);
                  e.stopPropagation();
                }}
                onBlur={commitName}
                onClick={(e) => e.stopPropagation()}
                style={{ all: 'unset', width: '120px' }}
              />
            );
          }

          if (value.id === 'suggestion') {
            const label =
              value.name && value.name !== value.email ? (
                <>
                  <strong>{value.name}</strong>
                  {` <${value.email}>`}
                </>
              ) : (
                value.email
              );
            return (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingName(
                      value.name && value.name !== value.email
                        ? value.name
                        : '',
                    );
                    setEditingEmail(value.email);
                  }}
                  style={{ all: 'unset', cursor: 'pointer', lineHeight: 0 }}
                  title="Add a display name"
                >
                  <Icon name="pen" size={12} />
                </button>
              </span>
            );
          }

          return value.name && value.email ? (
            <span>
              <strong>{value.name}</strong>
              {` <${value.email}>`}
            </span>
          ) : (
            (value.email ?? value.name ?? '')
          );
        },
      }}
      scope="users"
      initialArg={{
        sortBy: { field: 'display_name', order: 'asc' },
        pageSize: 20,
        isStrict: isEmail(filterValue),
        filters: {
          strategy: 'all',
          filters: [
            {
              field: 'user_type',
              operator: 'is_one',
              values: [{ value: 'member' }, { value: 'contact' }],
            },
            {
              field: 'email',
              operator: 'is_not_empty',
              values: [],
            },
          ],
        },
      }}
      getSuggestions={getSuggestions}
      transformResults={(users) =>
        users.flatMap((user) => {
          if (!user.additional_emails?.length) return [user];
          return [
            user,
            ...user.additional_emails.map((email) => ({
              ...user,
              id: `${user.id}:${email}`,
              email,
              additional_emails: [],
            })),
          ];
        })
      }
      transformValue={(user) => ({
        email: user.email?.trim() || '',
        name:
          user.id === 'suggestion'
            ? user.email?.trim() || ''
            : user.name || user.email?.trim() || '',
        id: user.id?.split(':')[0] || undefined,
      })}
      renderSelectionListItemContent={(user) => {
        if (!isSearchUser(user)) {
          return null;
        }

        return (
          <ContactItemContent
            id={user.id}
            name={user.name || ''}
            email={user.email || ''}
            avatarUrl={user.avatar_url ?? undefined}
          />
        );
      }}
    />
  );
};
